import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../Common/Button';
import ItemSearchFilters from './ItemSearchFilters';
import AddToCharacterModal from './AddToCharacterModal';
import ItemDetails from './ItemDetails';
import { createFuseInstance, performFuzzySearch, SearchResult } from '../../utils/fuseSearchConfig';
import './EquipmentBrowser.css'; // Reuse existing styles

// Generic item interface for both equipment and spells
export interface GenericItem {
  id: string;
  name: string;
  description?: string;
  category: string;
  type: string;
  tags?: string[];
  modifiers?: any[];
  // Equipment specific (optional)
  equipped?: boolean;
  // Spell specific (optional)
  domain?: string;
  level?: number;
  requirements?: string[];
  // Raw data for type-specific properties
  [key: string]: any;
}

interface FilterState {
  search: string;
  tags: string[];
  tiers: number[];
  levels: number[];
}

interface ItemBrowserProps {
  // Data loading
  loadItems: () => Promise<GenericItem[]>;
  
  // Display configuration
  category: string;
  itemType: 'equipment' | 'spell';
  libraryTitle: string;
  
  // Character integration
  characterId?: string;
  character?: any;
  baseCharacter?: any; // For duplicate detection
  onAddToCharacter?: (itemType: string, itemId: string) => Promise<void>;
  onRemoveFromCharacter?: (itemType: string, itemId: string) => Promise<void>;
  
  // Filtering configuration
  showTierFilter?: boolean;
  showLevelFilter?: boolean;
  getDefaultTiers?: () => number[];
  getDefaultLevels?: () => number[];
  
  // Display customization
  getCardDisplayInfo?: (item: GenericItem) => string;
}

const ItemBrowser: React.FC<ItemBrowserProps> = ({
  loadItems,
  category,
  itemType,
  libraryTitle,
  characterId,
  character,
  baseCharacter,
  onAddToCharacter,
  onRemoveFromCharacter,
  showTierFilter = false,
  showLevelFilter = false,
  getDefaultTiers = () => [1],
  getDefaultLevels = () => [1],
  getCardDisplayInfo,
}) => {
  // Helper function for title case formatting
  const toTitleCase = (str: string): string => {
    return str.replace(/\w\S*/g, (txt) => {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  };

  // Default card display function
  const defaultCardDisplayInfo = (item: GenericItem): string => {
    return toTitleCase(item.type);
  };

  // Check if item is already added to character using base character state
  // This prevents the every-other-operation pattern by using saved state instead of optimistic UI state
  const isAlreadyAdded = (item: GenericItem): boolean => {
    if (itemType === 'spell') {
      if (!baseCharacter?.activeSpells) return false;
      return baseCharacter.activeSpells.some((spell: any) => spell.id === item.id);
    } else if (itemType === 'equipment') {
      if (!baseCharacter?.inventory?.equipmentReferences) return false;
      return baseCharacter.inventory.equipmentReferences.some((ref: any) => ref.itemId === item.id);
    }
    return false;
  };

  // Get equipment quantity for display
  const getEquipmentQuantity = (item: GenericItem): number => {
    if (itemType !== 'equipment' || !baseCharacter?.inventory?.equipmentReferences) return 0;
    const ref = baseCharacter.inventory.equipmentReferences.find((ref: any) => ref.itemId === item.id);
    return ref ? ref.quantity : 0;
  };


  // State management
  const [items, setItems] = useState<GenericItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<GenericItem | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  
  // Pending operations for error handling (could be used for loading states)
  const [pendingOperations, setPendingOperations] = useState<Set<string>>(new Set());

  const [filters, setFilters] = useState<FilterState>({
    search: '',
    tags: [],
    tiers: getDefaultTiers(),
    levels: getDefaultLevels(),
  });

  // Track if this is initial load vs filtering
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load items data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        setIsInitialLoad(true);
        const loadedItems = await loadItems();
        setItems(loadedItems);
        // Allow initial animations to play, then disable for filtering
        setTimeout(() => setIsInitialLoad(false), 1000);
      } catch (err) {
        console.error('Failed to load items:', err);
        setError('Failed to load item data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [loadItems]);

  // Reset initial load state when category changes (weapons -> armor, etc.)
  useEffect(() => {
    setIsInitialLoad(true);
    setTimeout(() => setIsInitialLoad(false), 800);
  }, [category]);

  // Create Fuse instance for fuzzy search
  const fuseInstance = useMemo(() => {
    return createFuseInstance(items);
  }, [items]);

  // Filter items based on current filters with Fuse.js fuzzy search
  const filteredItems = useMemo(() => {
    let searchResults: GenericItem[] = items;
    
    // Apply fuzzy search if search term exists
    if (filters.search.trim()) {
      const fuzzyResults = performFuzzySearch(fuseInstance, filters.search);
      searchResults = fuzzyResults.map(result => result.item);
    }
    
    // Apply additional filters to search results
    return searchResults.filter((item) => {
      // Tags filter
      if (filters.tags.length > 0) {
        const itemTags = item.tags || [];
        if (!filters.tags.some(tag => itemTags.includes(tag))) {
          return false;
        }
      }

      // Tier filter
      if (filters.tiers.length > 0 && showTierFilter) {
        const itemTier = (item as any).tier;
        if (!itemTier || !filters.tiers.includes(itemTier)) {
          return false;
        }
      }

      // Level filter
      if (filters.levels.length > 0 && showLevelFilter) {
        const itemLevel = item.level || 1;
        if (!filters.levels.includes(itemLevel)) {
          return false;
        }
      }

      return true;
    });
  }, [items, fuseInstance, filters, showTierFilter, showLevelFilter]);

  // Event handlers
  const handleItemClick = (item: GenericItem) => {
    setSelectedItem(item);
  };

  const handleButtonClick = (item: GenericItem) => {
    if (isMobile) {
      // On mobile: show details modal
      setSelectedItem(item);
      setShowAddModal(true);
    } else {
      // On desktop: just select item (details show in sidebar)
      setSelectedItem(item);
    }
  };

  const handleAddToCharacterFromDesktop = async () => {
    if (!selectedItem || !characterId || !onAddToCharacter) return;
    
    const itemId = selectedItem.id;
    const itemName = selectedItem.name;
    
    // Mark operation as pending
    setPendingOperations(prev => new Set(prev).add(itemId));
    
    try {
      // Trigger the actual character update (this will update character state and trigger auto-save)
      await onAddToCharacter(itemType, itemId);
    } catch (error) {
      console.error('Failed to add item to character:', error);
    } finally {
      // Clear pending state
      setPendingOperations(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    }
  };

  const handleAddToCharacterFromModal = async () => {
    if (!selectedItem || !onAddToCharacter) return;
    
    const itemId = selectedItem.id;
    const itemName = selectedItem.name;
    
    // Mark operation as pending
    setPendingOperations(prev => new Set(prev).add(itemId));
    
    try {
      // Trigger the actual character update
      await onAddToCharacter(itemType, itemId);
    } catch (error) {
      console.error('Failed to add item to character:', error);
    } finally {
      // Clear pending state
      setPendingOperations(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    }
  };

  const handleRemoveFromCharacterFromDesktop = async () => {
    if (!selectedItem || !characterId || !onRemoveFromCharacter) return;
    
    const itemId = selectedItem.id;
    const itemName = selectedItem.name;
    
    // Mark operation as pending
    setPendingOperations(prev => new Set(prev).add(itemId));
    
    try {
      // Trigger the actual character update (this will update character state and trigger auto-save)
      await onRemoveFromCharacter(itemType, itemId);
    } catch (error) {
      console.error('Failed to remove item from character:', error);
    } finally {
      // Clear pending state
      setPendingOperations(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    }
  };

  if (loading) {
    return <div className="equipment-browser__loading">Loading {libraryTitle.toLowerCase()}...</div>;
  }

  if (error) {
    return <div className="equipment-browser__error">{error}</div>;
  }

  return (
    <motion.div 
      className="equipment-browser"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Search and Filters */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, delay: 0.1 }}
      >
        <ItemSearchFilters
          filters={filters}
          onFiltersChange={setFilters}
          availableTags={Array.from(new Set(filteredItems.flatMap(item => item.tags || [])))}
          characterTier={character?.tier}
          characterLevel={typeof character?.level === 'string' ? parseInt(character.level) : character?.level}
          showTierFilter={showTierFilter}
          showLevelFilter={showLevelFilter}
        />
      </motion.div>

      <div className="equipment-browser__content">
        {/* Item List */}
        <div className="equipment-browser__list">
          <motion.h3
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2, delay: 0.2 }}
          >
            {libraryTitle} ({filteredItems.length})
          </motion.h3>
          <motion.div 
            className="equipment-grid"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.3, staggerChildren: 0.05 }}
          >
              {filteredItems.map((item, index) => (
                <motion.div
                  key={item.id}
                  layout
                  initial={isInitialLoad ? { opacity: 0, scale: 0.8, y: 20 } : false}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  whileHover={{}}
                  whileTap={{ scale: 0.98 }}
                  transition={{ 
                    duration: isInitialLoad ? 0.2 : 0,
                    delay: isInitialLoad ? Math.min(index * 0.03, 0.5) : 0,
                    layout: { duration: 0.3 }
                  }}
                  className={`equipment-card ${selectedItem?.id === item.id ? 'equipment-card--selected' : ''}`}
                  onClick={() => handleItemClick(item)}
                >
                <div className="equipment-card__header">
                  <h4 className="equipment-card__name">{item.name}</h4>
                </div>
                <div className="equipment-card__category">
                  {getCardDisplayInfo ? getCardDisplayInfo(item) : defaultCardDisplayInfo(item)}
                </div>
                {itemType === 'equipment' && characterId && getEquipmentQuantity(item) > 0 && (
                  <div className="equipment-card__quantity">
                    Owned: {getEquipmentQuantity(item)}
                  </div>
                )}
                {characterId && isMobile && (
                  <Button
                    variant="PRIMARY"
                    size="SMALL"
                    onClick={() => handleButtonClick(item)}
                    className="equipment-card__add-button"
                  >
                    Show Details
                  </Button>
                )}
                </motion.div>
              ))}
          </motion.div>
        </div>

        {/* Item Details - Desktop only */}
        <AnimatePresence mode="wait">
          {selectedItem && (
            <motion.div 
              className="equipment-browser__details"
              key={selectedItem.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ 
                opacity: 0, 
                x: -10,
                transition: { duration: 0.1 }
              }}
              transition={{ duration: 0.2 }}
            >
              <ItemDetails
                item={selectedItem}
                characterId={characterId}
                showAddButton={!isMobile}
                onAddToCharacter={isAlreadyAdded(selectedItem) ? undefined : handleAddToCharacterFromDesktop}
                onRemoveFromCharacter={isAlreadyAdded(selectedItem) ? handleRemoveFromCharacterFromDesktop : undefined}
                buttonState={"default"}
                alreadyAdded={isAlreadyAdded(selectedItem)}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Add to Character Modal - Mobile only */}
      <AnimatePresence>
        {showAddModal && selectedItem && (
          <AddToCharacterModal
            item={selectedItem as any} // Type compatibility
            onConfirm={handleAddToCharacterFromModal}
            onCancel={() => {
              setShowAddModal(false);
              setSelectedItem(null);
            }}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ItemBrowser;